import { privateApiClient } from './apiClient';

/**
 * Service pour gérer les notifications
 * Note: Ce service utilise les endpoints /atv/notifications du mobile
 */

/**
 * Récupérer les notifications
 * @param {Object|string} params - Paramètres de recherche { pge, lmt, ntd, sts, vzn, chn, slg } ou destination (ntd) si string
 * @returns {Promise<Array>} Liste des notifications triées par date
 */
export const fetchNotifications = async (params = {}) => {
    // Si params est une string, c'est la destination (ntd)
    const normalizedParams = typeof params === 'string' 
        ? { ntd: params }
        : params;
    
    const defaultParams = {
        pge: 1,
        lmt: 20,
        sts: 'all',
        vzn: 'all',
        chn: 'all',
        slg: '',
        ...normalizedParams
    };
    
    const { data } = await privateApiClient.get('/atv/notifications', {
        params: defaultParams
    });
    
    // S'assurer que data est un tableau
    const notifications = Array.isArray(data) ? data : (data?.contents || []);
    
    // Trier par date et heure (plus récent en premier)
    const sorted = notifications.sort((a, b) => {
        const dateTimeA = new Date(`${a.date}T${a.time}`);
        const dateTimeB = new Date(`${b.date}T${b.time}`);
        return dateTimeB - dateTimeA;
    });
    
    return sorted;
};

/**
 * Marquer une notification comme lue
 * @param {string} notificationId - ID de la notification
 * @param {string} destination - ID de la destination (ntd)
 * @param {string} status - Statut (default: "viewed")
 * @returns {Promise<Object>} Réponse
 */
export const markNotificationAsRead = async (notificationId, destination, status = 'viewed') => {
    const { data } = await privateApiClient.get(`/atv/notifications/${notificationId}/mark-as`, {
        params: {
            ntd: destination,
            sts: status
        }
    });
    
    if (data?.error) {
        throw new Error(data.message || 'Erreur lors du marquage de la notification');
    }
    
    return data;
};

/**
 * Marquer une notification comme vue (PUT)
 * @param {string} notificationId - ID de la notification
 * @param {string} destination - ID de la destination (ntd)
 * @returns {Promise<Object>} Réponse
 */
export const markNotificationAsViewed = async (notificationId, destination) => {
    const { data } = await privateApiClient.put(
        `/atv/notifications/${notificationId}/mark-as-viewed`,
        null,
        {
            params: {
                ntd: destination
            }
        }
    );
    
    if (data?.error) {
        throw new Error(data.message || 'Erreur lors du marquage de la notification');
    }
    
    return data;
};

/**
 * Marquer toutes les notifications comme lues
 * @param {Object} params - Paramètres { sts, ntd, ids }
 * @returns {Promise<Object>} Réponse
 */
export const markAllNotificationsAsRead = async (params = {}) => {
    const { data } = await privateApiClient.get('/atv/notifications/mark-all-as', {
        params
    });
    
    if (data?.error) {
        throw new Error(data.message || 'Erreur lors du marquage des notifications');
    }
    
    return data;
};

/**
 * Récupérer le nombre de notifications par statut
 * @param {Object} params - Paramètres { ntd, sts }
 * @returns {Promise<Object>} Compteurs par statut
 */
export const fetchNotificationCountByStatus = async (params = {}) => {
    const { data } = await privateApiClient.get('/atv/notifications/count-by-status', {
        params
    });
    
    return data || {};
};

/**
 * Supprimer une notification
 * @param {string} notificationId - ID de la notification
 * @param {string} destination - ID de la destination
 * @returns {Promise<Object>} Réponse
 */
export const deleteNotification = async (notificationId, destination) => {
    const { data } = await privateApiClient.delete(`/pos/notifications/${notificationId}`, {
        params: { destination }
    });
    
    if (data?.error) {
        throw new Error(data.message || 'Erreur lors de la suppression de la notification');
    }
    
    return data;
};
